"use client"

import { Slider } from "@/components/ui/slider"
import { PARAM_RANGES } from "@/lib/cover-press/engine"

import { type Help } from "./help-copy"
import { HelpTip } from "./help-tip"

type SliderKey = keyof typeof PARAM_RANGES

interface RecipeSliderProps {
  paramKey: SliderKey
  label: string
  value: number
  onChange: (value: number) => void
  /** Readout formatter; defaults to two decimals for fractional ranges. */
  format?: (value: number) => string
  help?: Help
}

/**
 * One outcome-named knob: label and help glyph left, live readout right, the
 * kit slider beneath. Range and step come from the engine so a slider can
 * never push a recipe outside what a copied link would accept.
 */
export function RecipeSlider({
  paramKey,
  label,
  value,
  onChange,
  format,
  help,
}: RecipeSliderProps) {
  const { min, max, step } = PARAM_RANGES[paramKey]
  const display =
    format ??
    (step >= 1 ? (v: number) => String(v) : (v: number) => v.toFixed(2))

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <span className="text-secondary text-sm font-medium">{label}</span>
          {help && <HelpTip label={label} help={help} />}
        </span>
        <span className="text-tertiary text-xs tabular-nums">
          {display(value)}
        </span>
      </div>
      <Slider
        aria-label={label}
        minValue={min}
        maxValue={max}
        step={step}
        value={value}
        onChange={(v) => onChange(Array.isArray(v) ? v[0] : v)}
        labelFormatter={display}
      />
    </div>
  )
}
